import axios from 'axios'
import appConfig from '@/configs/app.config'
import { TOKEN_TYPE, REQUEST_HEADER_AUTH_KEY } from '@/constants/api.constant'
import { PERSIST_STORE_NAME } from '@/constants/app.constant'
import deepParseJson from '@/utils/deepParseJson'
import store, { signOutSuccess } from '../store'
import { logger, productionSafeCriticalLogger } from '@/utils/logger'
import { isDevelopment } from '@/utils/environment'

declare module 'axios' {
    export interface AxiosRequestConfig {
        /**
         * Set on requests whose caller already treats a failed status as an
         * expected, handled outcome — skips the shared response interceptor's
         * critical-error log for that request.
         */
        suppressErrorLog?: boolean
    }
}

const unauthorizedCode = new Set([401])

const BaseService = axios.create({
    timeout: 60000,
    baseURL: appConfig.apiPrefix,
    withCredentials: true,
})

BaseService.interceptors.request.use(
    (config) => {
        const requestStart = Date.now()
        // Store timing in a way that doesn't conflict with axios types
        ;(config as any).requestStart = requestStart

        // The log OBJECT (not just the logger call) is gated on dev: logger.debug
        // no-ops in prod, but its arguments would still be built on every request.
        if (isDevelopment) {
            logger.debug('API Request:', {
                method: config.method?.toUpperCase(),
                url: config.url,
                baseURL: config.baseURL,
                params: config.params,
                hasData: !!config.data,
                headers: {
                    ...config.headers,
                    [REQUEST_HEADER_AUTH_KEY]: config.headers[
                        REQUEST_HEADER_AUTH_KEY
                    ]
                        ? '[REDACTED]'
                        : undefined,
                },
            })
        }

        // On a first visit redux-persist hasn't written PERSIST_STORE_NAME yet,
        // so persistData is null — optional chaining lets us fall through to the store.
        const rawPersistData = localStorage.getItem(PERSIST_STORE_NAME)
        const persistData = deepParseJson(rawPersistData)

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let accessToken = (persistData as any)?.auth?.session?.token

        if (!accessToken) {
            const { auth } = store.getState()
            accessToken = auth.session.token
        }

        if (accessToken) {
            config.headers[REQUEST_HEADER_AUTH_KEY] =
                `${TOKEN_TYPE}${accessToken}`
            logger.debug('Added auth token to request')
        } else {
            logger.warn('No auth token available for request:', {
                url: config.url,
                method: config.method,
            })
        }

        return config
    },
    (error) => {
        logger.error('Request interceptor error:', {
            error: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
        })
        return Promise.reject(error)
    },
)

BaseService.interceptors.response.use(
    (response) => {
        const requestStart = (response.config as any).requestStart
        const duration = requestStart ? Date.now() - requestStart : undefined

        // Gated on dev because the dataSize field stringifies the WHOLE payload —
        // on large grid responses that's real per-response CPU wasted in prod
        // (logger.debug no-ops there, but arguments are evaluated regardless).
        if (isDevelopment) {
            logger.debug('API Response Success:', {
                method: response.config.method?.toUpperCase(),
                url: response.config.url,
                status: response.status,
                statusText: response.statusText,
                dataSize: response.data
                    ? JSON.stringify(response.data).length
                    : 0,
                duration: duration ? `${duration}ms` : undefined,
                headers: {
                    'content-type': response.headers['content-type'],
                    'content-length': response.headers['content-length'],
                },
            })
        }

        return response
    },
    (error) => {
        // Requests we abort on purpose (e.g. a superseded fetch when a DTS tab
        // changes) reject with a cancel error — that's expected, not a failure,
        // so skip the error logging and sign-out handling for it.
        if (axios.isCancel(error)) {
            return Promise.reject(error)
        }

        const { response, config } = error
        const requestStart = (config as any)?.requestStart
        const duration = requestStart ? Date.now() - requestStart : undefined

        if (response && unauthorizedCode.has(response.status)) {
            logger.warn('Unauthorized access detected, signing out user:', {
                status: response.status,
                url: config?.url,
                method: config?.method?.toUpperCase(),
            })
            store.dispatch(signOutSuccess())
        }

        // Some callers already treat a failed status as an expected outcome
        // they handle themselves (e.g. a lookup that 404s when there's
        // nothing to resolve) — those requests set `suppressErrorLog` so this
        // shared interceptor doesn't log them as a critical API error.
        if (!config?.suppressErrorLog) {
            // Use production-safe logging for API errors to prevent console spam in production
            productionSafeCriticalLogger.error('API Response Error:', {
                method: config?.method?.toUpperCase(),
                url: config?.url,
                status: response?.status,
                statusText: response?.statusText,
                errorMessage: error.message,
                responseData: response?.data,
                duration: duration ? `${duration}ms` : undefined,
                isNetworkError: !response,
                stack: error.stack,
            })
        }

        return Promise.reject(error)
    },
)

export default BaseService
